import { useLayoutEffect } from "react";

/**
 * Put the document back at the top, once, when the calling screen appears.
 *
 * The reveal is the only screen in a run that scrolls the page. Everything
 * before it is exactly viewport height with its panes scrolling inside
 * themselves, so nothing during a build is expected to move the document — but
 * "expected" is doing real work in that sentence. iOS Safari scrolls the
 * document itself as its toolbars collapse, and a visitor who nudged the page
 * during a four-minute build keeps that offset when the screen underneath them
 * is swapped for a taller one. React replaces the tree; it does not touch the
 * scroll position, because it has no way to know the new tree is a new page.
 *
 * The result is the reveal opening halfway down itself — past the band that
 * says it worked, past the address, and for a texted visitor past the code they
 * came for. The screen looks broken in the one moment it has to look finished.
 *
 * `useLayoutEffect` rather than `useEffect` so the correction lands BEFORE the
 * browser paints. On `useEffect` the wrong position is painted first and then
 * jumps, which reads as a glitch rather than as arriving at the top.
 *
 * Instant rather than smooth, and deliberately not honouring a smooth-scroll
 * preference: this is not a journey the visitor asked to take, it is where the
 * page should have been to begin with. Animating it would draw the eye to a
 * correction nobody should notice.
 *
 * Guarded because it touches a browser global that is not always real: jsdom
 * implements `scrollTo` as a throwing stub, and a screen must never fail to
 * mount over where it happens to be scrolled.
 */
export function useScrollToTop(): void {
  useLayoutEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    } catch {
      // Older browsers reject the options form; the positional one is ancient.
      try {
        window.scrollTo(0, 0);
      } catch {
        // No scrolling available at all (jsdom). Nothing to correct.
      }
    }
  }, []);
}
